import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { ListChecks, ClipboardList, Sparkles, CheckCircle2, AlertTriangle, CalendarClock, ArrowRight, Filter, Inbox, Plus } from "lucide-react";
import { Button } from "../components/ui/button";
import { QuickTaskDialog } from "../components/project/QuickTaskDialog";
import { useAuth } from "../context/AuthContext";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { StatusPill } from "../components/StatusPill";
import { PRIORITY_COLORS, TASK_STATUS_COLORS, REQ_STATUS_COLORS, TASK_STATUSES } from "../components/project/projectUtils";

const BUCKETS = [
  { key: "overdue", label: "Overdue", icon: AlertTriangle, tint: "text-rose-700" },
  { key: "today", label: "Due Today", icon: Sparkles, tint: "text-orange-700" },
  { key: "week", label: "This Week", icon: CalendarClock, tint: "text-blue-700" },
  { key: "later", label: "Later", icon: CalendarClock, tint: "text-neutral-700" },
  { key: "none", label: "No Due Date", icon: Inbox, tint: "text-neutral-600" },
  { key: "done", label: "Completed", icon: CheckCircle2, tint: "text-emerald-700" },
];

export default function MyWork() {
  const { user } = useAuth();
  const [data, setData] = useState({ tasks: [], requirements: [], content: [], counts: { total: 0, tasks: 0, requirements: 0, content: 0 } });
  const [tab, setTab] = useState("all");
  const [loading, setLoading] = useState(true);
  const [quickOpen, setQuickOpen] = useState(false);
  const canCreateTask = ["admin", "ceo", "coo", "manager", "project_manager", "developer", "tester"].includes(user?.role);

  const load = async () => {
    try {
      const { data } = await api.get("/my-work");
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const isAdminOrManager = ["admin", "manager"].includes(user?.role);

  // Unified items
  const items = useMemo(() => {
    const out = [];
    for (const t of data.tasks) {
      out.push({
        kind: "task",
        id: t.id,
        title: t.name,
        ref: t.task_id,
        due: t.due_date,
        status: t.status,
        priority: t.priority,
        source: t.project_name || t.project_ref || "Project",
        link: `/projects/${t.project_id}`,
        raw: t,
      });
    }
    for (const r of data.requirements) {
      out.push({
        kind: "requirement",
        id: r.id,
        title: r.title,
        ref: r.requirement_id,
        due: r.deadline,
        status: r.status,
        priority: r.priority,
        source: r.project_name || r.project_ref || "Project",
        link: `/projects/${r.project_id}`,
        raw: r,
      });
    }
    for (const c of data.content) {
      out.push({
        kind: "content",
        id: c.id,
        title: c.content_topic || c.content_type,
        ref: c.content_id,
        due: c.publishing_date,
        status: c.status,
        priority: "",
        source: c.brand_name || c.client_name || "Client",
        link: `/clients/${c.client_id}`,
        raw: c,
      });
    }
    return out;
  }, [data]);

  const filtered = items.filter(i => tab === "all" || i.kind === tab);
  const bucketed = useMemo(() => bucketItems(filtered), [filtered]);

  const setTaskStatus = async (task, status) => {
    try {
      await api.put(`/tasks/${task.id}`, { status });
      toast.success("Updated");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="p-6 md:p-8 space-y-6 max-w-[1600px]">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[11px] font-mono uppercase tracking-[0.2em] text-orange-400">Your queue</div>
          <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight mt-1">My Work</h1>
          <p className="text-neutral-600 text-sm mt-1">
            {loading ? "Loading…" : (
              data.counts.total === 0
                ? "Inbox zero. Enjoy the calm."
                : `${data.counts.total} item${data.counts.total === 1 ? "" : "s"} on your plate right now`
            )}
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="text-[11px] font-mono text-neutral-500 uppercase tracking-wider">
            Signed in as {user?.name} · {user?.role?.replace(/_/g, " ")}
          </div>
          {canCreateTask && (
            <Button onClick={() => setQuickOpen(true)} className="bg-orange-500 hover:bg-orange-600 text-white" size="sm" data-testid="mywork-new-task-btn">
              <Plus className="w-4 h-4 mr-1" /> New Task
            </Button>
          )}
        </div>
      </div>
      <QuickTaskDialog open={quickOpen} onOpenChange={setQuickOpen} currentUser={user} onCreated={load} />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <SummaryCard icon={ListChecks} label="Total" value={data.counts.total} tint="orange" testid="mw-total" />
        <SummaryCard icon={ClipboardList} label="Tasks" value={data.counts.tasks} tint="blue" testid="mw-tasks" />
        <SummaryCard icon={Filter} label="Requirements" value={data.counts.requirements} tint="violet" testid="mw-reqs" />
        <SummaryCard icon={Sparkles} label={isAdminOrManager ? "Pending Approvals" : "Content"} value={data.counts.content} tint="emerald" testid="mw-content" />
      </div>

      <Tabs value={tab} onValueChange={setTab} className="space-y-4">
        <TabsList className="bg-[#FFFFFF] border border-[#E4E7EC]">
          <TabsTrigger value="all" data-testid="mw-tab-all">All ({data.counts.total})</TabsTrigger>
          <TabsTrigger value="task" data-testid="mw-tab-tasks">Tasks ({data.counts.tasks})</TabsTrigger>
          <TabsTrigger value="requirement" data-testid="mw-tab-reqs">Requirements ({data.counts.requirements})</TabsTrigger>
          <TabsTrigger value="content" data-testid="mw-tab-content">{isAdminOrManager ? "Approvals" : "Content"} ({data.counts.content})</TabsTrigger>
        </TabsList>
      </Tabs>

      {filtered.length === 0 ? (
        <div className="panel p-12 text-center text-neutral-500">
          <Inbox className="w-10 h-10 mx-auto mb-4 opacity-40" />
          <div className="font-mono text-sm uppercase tracking-wider">Nothing here. You're all clear.</div>
        </div>
      ) : (
        <div className="space-y-6">
          {BUCKETS.map(b => {
            const list = bucketed[b.key];
            if (!list || list.length === 0) return null;
            return (
              <section key={b.key} data-testid={`mw-bucket-${b.key}`}>
                <div className="flex items-center gap-2 mb-2 px-1">
                  <b.icon className={`w-3.5 h-3.5 ${b.tint}`} />
                  <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-neutral-600">{b.label}</span>
                  <span className="text-[10px] font-mono text-neutral-600">· {list.length}</span>
                </div>
                <div className="space-y-2">
                  {list.map(item => (
                    <Row key={`${item.kind}-${item.id}`} item={item} onTaskStatus={setTaskStatus} />
                  ))}
                </div>
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
}

function bucketItems(items) {
  const now = new Date(); now.setHours(0,0,0,0);
  const inDays = (d) => {
    if (!d) return null;
    const t = new Date(d); t.setHours(0,0,0,0);
    return Math.round((t - now) / 86400000);
  };
  const out = { overdue: [], today: [], week: [], later: [], none: [], done: [] };
  for (const i of items) {
    if (["Completed", "Published"].includes(i.status)) { out.done.push(i); continue; }
    const days = inDays(i.due);
    if (days === null) out.none.push(i);
    else if (days < 0) out.overdue.push(i);
    else if (days === 0) out.today.push(i);
    else if (days <= 7) out.week.push(i);
    else out.later.push(i);
  }
  return out;
}

function Row({ item, onTaskStatus }) {
  const kindMeta = {
    task: { icon: ListChecks, tint: "from-blue-500/30 to-blue-500/10 border-blue-500/20 text-blue-700", label: "TASK" },
    requirement: { icon: ClipboardList, tint: "from-violet-500/30 to-violet-500/10 border-violet-500/20 text-violet-700", label: "REQ" },
    content: { icon: Sparkles, tint: "from-orange-500/30 to-orange-500/10 border-orange-500/20 text-orange-700", label: "POST" },
  }[item.kind];
  const StatusChip = () => {
    if (item.kind === "task") {
      const cls = TASK_STATUS_COLORS[item.status] || "";
      return <span className={`text-[10px] font-mono px-2 py-0.5 rounded border ${cls}`}>{item.status}</span>;
    }
    if (item.kind === "requirement") {
      const cls = REQ_STATUS_COLORS[item.status] || "";
      return <span className={`text-[10px] font-mono px-2 py-0.5 rounded border ${cls}`}>{item.status}</span>;
    }
    return <StatusPill status={item.status} />;
  };

  return (
    <div className="panel p-3 pl-4 flex items-center gap-3" data-testid={`mw-row-${item.kind}-${item.id}`}>
      <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${kindMeta.tint} border grid place-items-center shrink-0`}>
        <kindMeta.icon className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[9px] font-mono text-neutral-500">{kindMeta.label} · {item.ref}</span>
          {item.priority && <span className={`text-[9px] font-mono px-1.5 py-0.5 rounded border ${PRIORITY_COLORS[item.priority] || ""}`}>{item.priority}</span>}
        </div>
        <div className="text-sm font-medium truncate">{item.title}</div>
        <div className="text-[10px] font-mono text-neutral-500 uppercase truncate">{item.source}{item.due ? ` · due ${item.due}` : ""}</div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {item.kind === "task" ? (
          <select
            className="text-[11px] font-mono bg-[#FFFFFF] border border-[#E4E7EC] rounded px-2 py-1"
            value={item.status}
            onChange={e => onTaskStatus(item.raw, e.target.value)}
            data-testid={`mw-task-status-${item.id}`}>
            {TASK_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        ) : (
          <StatusChip />
        )}
        <Link to={item.link} className="text-orange-400 hover:text-orange-700" data-testid={`mw-open-${item.kind}-${item.id}`}>
          <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </div>
  );
}

function SummaryCard({ icon: Icon, label, value, tint, testid }) {
  const tints = {
    orange: "from-orange-500/20 to-orange-500/5 border-orange-500/20 text-orange-700",
    blue: "from-blue-500/20 to-blue-500/5 border-blue-500/20 text-blue-700",
    violet: "from-red-500/20 to-red-500/5 border-red-500/20 text-red-700",
    emerald: "from-emerald-500/20 to-emerald-500/5 border-emerald-500/20 text-emerald-700",
  }[tint];
  return (
    <div className="panel p-5 relative overflow-hidden" data-testid={testid}>
      <div className={`absolute -top-8 -right-8 w-24 h-24 rounded-full bg-gradient-to-br ${tints} opacity-40 blur-2xl`} />
      <div className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md border ${tints} bg-gradient-to-br ${tints} font-mono text-[10px] uppercase tracking-wider`}>
        <Icon className="w-3.5 h-3.5" /> {label}
      </div>
      <div className="mt-3 text-3xl font-extrabold tracking-tight">{value}</div>
    </div>
  );
}
